import { Location } from '@angular/common';
import {
  Component,
  EffectRef,
  ElementRef,
  Inject,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  ProviderToken,
  ViewChild,
  effect,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import { ProviderClass, UiZoneWidgetAttributeConfig } from '@finxone-platform/shared/sys-config-types';
import {
  WidgetContext,
  WidgetHost,
  WidgetHostHandlers,
  createWidgetHost,
} from '@finxone-platform/shared/app-sdk';
import { Store } from '@ngxs/store';
import { AccountService } from '../../../../lib/services/account-service/account-service.service';
import { CardManagementService } from '../../../../lib/services/card-management-service/card-management-service';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { CustomFunctionsService } from '../../../../lib/services/custom-functions/custom-functions.service';
import { CompaniesHouseService } from '../../../../lib/services/companies-house-service/companies-house.service';
import { DataSourceService } from '../../../../lib/services/data-source-service/data-source.service';
import { DocumentService } from '@app/finxone-web-frontend/app/lib/services/document-service/document.service';
import { NotificationService } from '@app/finxone-web-frontend/app/lib/services/notification-service/notification-service.service';
import { PasswordService } from '@app/finxone-web-frontend/app/lib/services/password-service/password-service.service';
import { DownloadDocumentService } from '../../../../lib/services/download-document/download-document.service';
import { ExchangeService } from '../../../../lib/services/exchange-service/exchange-service.service';
import { IdentityVerificationService } from '../../../../lib/services/identity-verification/identity-verification.service';
import { HeadlessAuthFlowService } from '../../../../lib/services/headless-auth-flow/headless-auth-flow.service';
import { KeycloakWrapperService } from '../../../../lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { MetadataService } from '../../../../lib/services/metadata-service/metadata-service.service';
import { NavigationService } from '../../../../lib/services/navigation-service/navigation.service';
import { SavingsAccountService } from '../../../../lib/services/savings-account-service/savings-account.service';
import { SocketService } from '../../../../lib/services/socket-service/socket-service.service';
import { TransactionService } from '../../../../lib/services/transaction-service/transaction.service';
import { ProfileState } from '../../../../lib/state/user-profile.state';
import { ProjectSettingsState } from '../../../../lib/state/project-settings.state';
import { WidgetHandoffService } from '../../../../lib/services/widget-handoff/widget-handoff.service';
import { WorkflowFormsService } from '../../../../lib/services/workflow-forms/workflow-forms.service';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { OverlayConfirmationService } from '../../../../lib/services/overlay-confirmation-service/overlay-confirmation.service';
import { createOverlayConfirmationRunner } from './custom-widget-confirmation';
import {
  CustomWidgetInstanceProperties,
  buildCustomWidgetHostHandlers,
  checkCustomWidgetCompatibility,
  resolveGrants,
} from './custom-widget-host.utils';
import { withHandoffsTopic } from './custom-widget-handoff';
import { buildCustomWidgetPreviewContext, createCustomWidgetPreviewHandlers } from './custom-widget-preview';
import {
  CUSTOM_WIDGET_PREVIEW_URL_RESOLVER,
  CustomWidgetPreviewUrlResolver,
} from './custom-widget-preview-url';
import { resolveCustomWidgetFrame } from './custom-widget-browser-frame';

/**
 * Renders an uploaded custom widget bundle in a sandboxed iframe and bridges
 * it to the platform over the fx-widget protocol. The frame has an opaque
 * origin (sandbox without allow-same-origin), so the init message must be
 * posted with targetOrigin '*' — the capability boundary is the transferred
 * MessagePort plus the grants, not the origin check.
 *
 * Two host paths, chosen once per frame load and never mixed. Both build their
 * handlers from the same bridge over services resolved from this component's
 * injector — which is the point: on the app builder canvas that injector is the
 * canvas's child injector, whose providers are the builder's Mock* services (see
 * mock-environment.provider.ts in the workflow frontend), so a previewed widget
 * exercises the real bridge against mock data rather than a second, divergent
 * fixture host.
 *
 * - production (`isBuilderMode === false`): createPlatformHost() — the real
 *   services, real navigation, real confirmations.
 * - app builder canvas (`isBuilderMode === true`): createBuilderHost() — the
 *   same bundle in the same sandboxed frame and the same bridge, minus the
 *   services whose side effects would land on the *builder*: navigation,
 *   hand-offs, alerts, downloads, identity and the session. Those groups are
 *   served by createCustomWidgetPreviewHandlers(); the omitted services degrade
 *   to the bridge's own host-error path, the same one a project that wires none
 *   of them gets.
 */
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-custom-widget',
  templateUrl: './custom-widget.component.html',
  styleUrls: ['./custom-widget.component.scss'],
})
export class CustomWidgetComponent implements OnInit, OnDestroy {
  static showMargin = true;
  static showPadding = true;

  @ViewChild('frame') frameRef?: ElementRef<HTMLIFrameElement>;

  instance?: CustomWidgetInstanceProperties;
  frameUrl?: SafeResourceUrl;
  frameUsesBrowserOrigin = false;
  frameAllow: string | null = null;
  heightPx = 320;
  isBuilderMode = false;
  /**
   * Set when the activated bundle cannot run against this host's SDK. Truthy means
   * no frame was created at all — see ngOnInit.
   */
  incompatible = false;
  /** Customer-facing line for the incompatible placeholder. Never the raw reason. */
  readonly incompatibleMessage = 'This widget was built against an older SDK version and needs republishing.';

  private host?: WidgetHost;
  private frameTargetOrigin = '*';
  /** Builder-only: keeps the previewed widget in step with theme edits. */
  private themeEffect?: EffectRef;

  /**
   * The services the handlers are built from are resolved from the injector
   * rather than taken as constructor parameters, so the builder canvas's child
   * injector can substitute its Mock* providers for them without this component
   * knowing the app builder exists.
   */
  constructor(
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private store: Store,
    private configService: ConfigService,
    private sanitizer: DomSanitizer,
    private zone: NgZone,
    private injector: Injector,
    @Optional() private zonePageUrl: ZonePageUrlProvider | null,
    @Optional()
    @Inject(CUSTOM_WIDGET_PREVIEW_URL_RESOLVER)
    private previewUrlResolver: CustomWidgetPreviewUrlResolver | null,
  ) {}

  ngOnInit(): void {
    this.isBuilderMode = this.configService.getActiveConfigType().isWorkflow;
    this.instance = this.dataIn?.attri?.widgetProperties?.['customWidget'] as
      CustomWidgetInstanceProperties | undefined;
    if (!this.instance) return;
    this.heightPx = this.instance.defaultHeight ?? this.heightPx;
    // Gate 3 of 3, and the one that actually protects a customer: the registry can
    // only refuse what passes through it, and a project's pages keep whatever was
    // placed on them. Checked BEFORE the frame exists, so an incompatible bundle is
    // never fetched, never mounted and never handed a port — a broken widget is
    // worse than an honest placeholder.
    const compatibility = checkCustomWidgetCompatibility(this.instance);
    if (!compatibility.compatible) {
      this.incompatible = true;
      // The reason belongs in the console for whoever has to fix it; the customer
      // gets incompatibleMessage. No stack trace either way — nothing threw.
      console.warn(
        `[custom-widget] Not rendering ${this.instance.widgetId}@${this.instance.version}: ${compatibility.reason}`,
      );
      return;
    }
    const frameSrc = this.resolveFrameSrc(this.instance);
    if (!frameSrc) return;
    // bundleUrl is admin-authored config served same-origin, not user input
    this.frameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(frameSrc);
  }

  /**
   * Production always uses the persisted `bundleUrl`: no resolver is provided there, so this
   * returns exactly what it always did.
   *
   * The app builder is the one caller that cannot use `bundleUrl` — that path only exists in a
   * deployed project's web-frontend — so it provides a resolver pointing at the provisioner's
   * bundle route. Resolution happens here, per render, and is never persisted into the widget
   * config: workflow_config must not carry a builder-only or localhost URL.
   */
  private resolveFrameSrc(instance: CustomWidgetInstanceProperties): string | undefined {
    if (this.isBuilderMode) {
      if (this.previewUrlResolver && instance.widgetId && instance.version) {
        const resolved = this.previewUrlResolver.resolve({
          widgetId: instance.widgetId,
          version: instance.version,
        });
        if (resolved) {
          this.frameUsesBrowserOrigin = false;
          this.frameAllow = null;
          this.frameTargetOrigin = '*';
          return resolved;
        }
      }
      // Legacy builder hosts do not install the resolver. Keep their existing
      // opaque preview behavior and, critically, do not read live project state.
      return instance.bundleUrl;
    }
    const activation = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.projectConfig
      ?.account_service?.embedConfig?.widgets?.[instance.widgetId];
    const resolution = resolveCustomWidgetFrame(
      instance,
      activation,
      this.configService.config?.customWidgetEdge,
      this.configService.config?.features?.customWidgetBrowserCapabilitiesEnabled === true,
    );
    if (!resolution) return undefined;
    this.frameUsesBrowserOrigin = resolution.sandbox.includes('allow-same-origin');
    this.frameAllow = resolution.allow;
    this.frameTargetOrigin = resolution.targetOrigin;
    return resolution.src;
  }

  onFrameLoad(): void {
    const frameWindow = this.frameRef?.nativeElement.contentWindow;
    if (!frameWindow || !this.instance) return;
    // The builder recreates these components on every config edit, and a frame
    // can also fire load more than once, so tear the previous host (port +
    // topic subscriptions + theme effect) down before opening another.
    this.teardownHost();
    this.host = this.isBuilderMode
      ? this.createBuilderHost(this.instance)
      : this.createPlatformHost(this.instance);
    this.host.initInto(frameWindow, this.frameTargetOrigin);
  }

  ngOnDestroy(): void {
    this.teardownHost();
  }

  private teardownHost(): void {
    this.themeEffect?.destroy();
    this.themeEffect = undefined;
    this.host?.dispose();
    this.host = undefined;
  }

  /** Production path: the real platform handlers. Unchanged by the preview. */
  private createPlatformHost(instance: CustomWidgetInstanceProperties): WidgetHost {
    const context = this.buildContext(instance);
    return createWidgetHost({
      context,
      grants: resolveGrants(instance),
      handlers: this.buildBridgeHandlers(context, { builderMode: false }),
    });
  }

  /**
   * Builder canvas path: the same bridge, over whatever the canvas injector
   * provides — the app builder's Mock* services — with the handler groups that
   * would act on the builder itself replaced by the preview ones.
   */
  private createBuilderHost(instance: CustomWidgetInstanceProperties): WidgetHost {
    const context = buildCustomWidgetPreviewContext({
      instance,
      instanceId: String(this.dataIn?.uniqueId ?? ''),
      page: {
        zone: this.zonePageUrl?.zoneUrlInput ?? '',
        url: this.zonePageUrl?.pageUrlInput ?? '',
        role: this.zonePageUrl?.role ?? '',
      },
      theme: this.configService.globalCssVariables(),
      locale: navigator.language,
    });
    const bridge = this.buildBridgeHandlers(context, { builderMode: true });
    const preview = createCustomWidgetPreviewHandlers();
    let sentTheme = context.theme;
    const host = createWidgetHost({
      context,
      grants: resolveGrants(instance),
      handlers: {
        ...bridge,
        // The bridge builds no hand-off topic without a WidgetHandoffService, so
        // 'handoffs' is routed to the in-memory preview one instead.
        data: withHandoffsTopic(bridge.data as NonNullable<WidgetHostHandlers['data']>, preview.handoffs),
        nav: preview.nav,
        ui: preview.ui,
        handoff: preview.handoff,
        state: preview.state,
      },
    });
    // Theme tokens are a signal, so following builder theme edits costs one
    // effect; the widget re-renders itself off the pushed 'theme' stream. The
    // init message already carried the current tokens, hence the identity guard.
    this.themeEffect = effect(
      () => {
        const theme = this.configService.globalCssVariables();
        if (theme === sentTheme || this.host !== host) return;
        sentTheme = theme;
        host.pushContext('theme', theme);
      },
      { injector: this.injector },
    );
    return host;
  }

  /**
   * The platform handlers for one mounted instance.
   *
   * In builder mode the services that would act on the *builder* are not
   * resolved at all — not passed as no-ops, not resolved and ignored: a
   * DownloadDocumentService or KeycloakWrapperService constructed on the canvas
   * would be a real HTTP client pointed at a real backend. Their operations then
   * take the bridge's own "service not wired" path, which is the same clean
   * host-error a deployed project that grants none of them produces.
   */
  private buildBridgeHandlers(context: WidgetContext, options: { builderMode: boolean }): WidgetHostHandlers {
    const platformOnly = <T>(token: ProviderToken<T>): T | undefined =>
      options.builderMode ? undefined : this.resolve(token);
    return buildCustomWidgetHostHandlers({
      // Hand-off records and sdk.state are scoped to this mounted instance.
      scope: { widgetId: context.widgetId, instanceId: context.instanceId },
      store: this.store,
      accountService: this.require(AccountService),
      cardManagementService: this.require(CardManagementService),
      transactionService: this.require(TransactionService),
      savingsAccountService: this.require(SavingsAccountService),
      exchangeService: this.require(ExchangeService),
      navigationService: platformOnly(NavigationService),
      router: platformOnly(Router),
      location: platformOnly(Location),
      workflowFormsService: this.require(WorkflowFormsService),
      alertHandlerService: this.require(AlertHandlerService),
      configService: this.configService,
      handoffService: platformOnly(WidgetHandoffService),
      confirmationRunner: options.builderMode
        ? undefined
        : createOverlayConfirmationRunner(this.require(OverlayConfirmationService)),
      downloadDocumentService: platformOnly(DownloadDocumentService),
      // The customer's stored documents, their notification feed and their password. All
      // platform-only: on the builder canvas each would be a real call against a real customer.
      documentService: platformOnly(DocumentService),
      notificationService: platformOnly(NotificationService),
      passwordService: platformOnly(PasswordService),
      // Serves the onboarding operations and the onboardingStatus topic. The
      // identity step is host-rendered like the card-secret reveal: the
      // provider token and the vendor session never reach the bundle.
      metadataService: this.resolve(MetadataService),
      companiesHouseService: this.resolve(CompaniesHouseService),
      dataSourceService: this.resolve(DataSourceService),
      identityVerificationService: platformOnly(IdentityVerificationService),
      // Serves auth.login / auth.logout and the submit follow-up. auth.status
      // needs neither — it reads the profile slice.
      keycloakWrapperService: platformOnly(KeycloakWrapperService),
      // Serves auth.flow.* — the managed login flow. Platform-only for the same
      // reason as the wrapper above: on the canvas it would be a real client
      // talking to a real Keycloak.
      headlessAuthFlow: platformOnly(HeadlessAuthFlowService),
      socketService: this.resolve(SocketService),
      // Serves sdk.functions.invoke. Platform-only: a function runs real
      // server-side code as the signed-in user, which the builder canvas must
      // not do on a builder's session.
      customFunctionsService: platformOnly(CustomFunctionsService),
      // port messages arrive outside the Angular zone
      onResize: (height) => this.zone.run(() => (this.heightPx = height)),
    });
  }

  /** A service the bridge cannot be built without; absent, this throws as the constructor did. */
  private require<T>(token: ProviderToken<T>): T {
    return this.injector.get(token);
  }

  /** A service whose absence the bridge already degrades cleanly for. */
  private resolve<T>(token: ProviderToken<T>): T | undefined {
    return this.injector.get(token, null, { optional: true }) ?? undefined;
  }

  private buildContext(instance: CustomWidgetInstanceProperties) {
    return {
      widgetId: instance.widgetId,
      instanceId: String(this.dataIn?.uniqueId ?? ''),
      settings: instance.settings ?? {},
      page: {
        zone: this.zonePageUrl?.zoneUrlInput ?? '',
        url: this.zonePageUrl?.pageUrlInput ?? '',
        role: this.zonePageUrl?.role ?? '',
      },
      theme: this.configService.globalCssVariables(),
      locale: navigator.language,
      user: {
        // The profile slice is the fallback, and in practice the only source that
        // answers: the zone injector's `userProfileData` is fed from a field the
        // widget-layout components never assign, so it is `{}` on every page. It
        // is still preferred when present, and this is the same slice the `profile`
        // topic and sdk.auth.status() read — one source, no disagreement.
        id: this.dataIn?.userProfileData?.id || this.store.selectSnapshot(ProfileState.getProfile)?.id || '',
        // The *page's* role segment, not a session claim — an anonymous visitor to
        // a /customer/… onboarding page has one too. sdk.auth.status() is the only
        // honest answer to "who is signed in".
        roles: this.zonePageUrl?.role ? [this.zonePageUrl.role] : [],
      },
    };
  }
}
